import { Component, DestroyRef, effect, inject, input, linkedSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideSearch } from '@ng-icons/lucide';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { toast } from 'ngx-sonner';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Référentiel } from '../../../../../administration/gestion-référentiels/models/référentiel.model';
import { SidepanelService } from '../../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../../shared/components/toastr/toastr-success.component';
import { Phase } from '../../../processus-phases/models/phase.model';
import { IndiceRésultats } from '../../models/indice-résultats';
import { IndiceUpdateRequest } from '../../requests/update-request';
import { IndiceRésultatsStore } from '../../store/indice-résultats.store';

@Component({
  selector: 'app-form-indice',
  imports: [
    ReactiveFormsModule,
    HlmIconImports,
    HlmInputImports,
    HlmFieldImports,
    HlmSelectImports,
    BrnSelectImports,
    HlmInputGroupImports,
    LabelRequiredDirective,
  ],
  templateUrl: './form-indice.component.html',
  providers: [provideIcons({ lucideSearch })],
})
export class FormIndiceComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #indiceStore = inject(IndiceRésultatsStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);

  readonly indiceResultats = input.required<IndiceRésultats>();
  readonly referentiel = input.required<Référentiel>();
  readonly phases = input.required<Phase[]>();
  readonly aptitudesFiltrees = linkedSignal(() =>
    this.phases()
      .filter((x) => x.aptitudes.some((x) => x.indicesResultats.length > 0))
      .map((x) => ({ nom: x.nom, items: x.aptitudes })),
  );
  readonly form = this.#fb.group({
    nom: [null, Validators.required],
    aptitude: [null, Validators.required],
    nomAptitude: [''],
  });

  constructor() {
    effect(() => {
      this.nom.setValue(this.indiceResultats().nom);
      this.aptitude.setValue(this.indiceResultats().idAptitude);
    });

    effect(() => this.#indiceStore.charger(this.referentiel().id));
  }

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());

    this.nomAptitude.valueChanges
      .pipe(debounceTime(10), takeUntilDestroyed(this.#destroyRef), distinctUntilChanged())
      .subscribe(() => {
        this.aptitudesFiltrees.set(
          this.phases()
            .filter((x) => x.aptitudes.some((x) => x.indicesResultats.length > 0))
            .map((x) => ({ nom: x.nom, items: x.aptitudes }))
            .filter(
              (group) =>
                group.items.filter((x) => x.nom.toLowerCase().includes(this.nomAptitude.value.toLowerCase())).length >
                0,
            ),
        );
      });
  }

  onSubmit() {
    if (this.nom.hasError('required')) {
      return;
    }
    const toastrId = uuidv7();

    const data: IndiceUpdateRequest = {
      idAptitude: this.aptitude.value,
      indices: [
        {
          nom: this.nom.value,
          id: this.indiceResultats().id,
          indice: this.indiceResultats().indice,
        },
      ],
    };

    if (this.alreadyExists(data)) return;

    this.#indiceStore.modifier({
      data,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: { message: "Modification de l'indice de résultats réussie", id: toastrId },
          id: toastrId,
        });
        this.#sidepanelService.close();
        this.#indiceStore.charger(this.referentiel().id);
      },
      error: () => {},
    });
  }

  alreadyExists(indiceAModifier: IndiceUpdateRequest): boolean {
    const indices = this.#indiceStore
      .entities()
      .filter((x) => x.idAptitude === indiceAModifier.idAptitude)
      .map((x) => ({
        nom: x.nom,
        idAptitude: x.idAptitude,
        id: x.id,
      }));

    const doublonsAvecIndicesExistants = indices.filter((x) =>
      indiceAModifier.indices.map((y) => y.nom).includes(x.nom),
    );

    if (doublonsAvecIndicesExistants.length > 0) {
      this.nom.setErrors({ notUnique: true });
      this.form.markAllAsTouched();
      return true;
    }

    return false;
  }

  get nom(): AbstractControl {
    return this.form.get('nom') as AbstractControl;
  }

  get aptitude(): AbstractControl {
    return this.form.get('aptitude') as AbstractControl;
  }

  get nomAptitude(): AbstractControl {
    return this.form.get('nomAptitude') as AbstractControl;
  }
}
